import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MessageCircleWarning } from "lucide-react";
import { detectInAppBrowser } from "@/lib/inAppBrowser";

/**
 * CheckoutFeedback — bottom-right nudge card that only surfaces when we have
 * **evidence** a shopper hit a checkout problem. Three triggers:
 *
 *   1. `checkout_error` — the /api/payments/checkout call failed OR a client-
 *      side JS error was caught on the checkout path. Fires immediately.
 *      Listens for `window.dispatchEvent(new CustomEvent('tracksy:checkout-error', {detail:{reason}}))`.
 *
 *   2. `abandoned` — shopper tapped Checkout but hasn't landed on
 *      /payment/success within 10 min. We stamp `tracksy:ck:lastAttempt` in
 *      localStorage when the Checkout button is clicked (see CartDrawer).
 *      On any mount after 10 min with no success flag, this card appears.
 *
 *   3. `return_visit` — shopper returns ≥30 min later with items still in
 *      their cart but no `tracksy:ck:lastAttempt`.
 *
 * One submission per session (dismissed/stored dismissed) so we never pester.
 * Every submission POSTs to /api/support/checkout-feedback with session_id +
 * cart snapshot + platform + country + webview source + user_agent so the
 * admin inbox has full context for a reply.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COPY = {
  checkout_error: {
    headline: "That didn't look right",
    body: "Something went wrong when you tapped Checkout. Mind telling us what you saw? We'll fix it + sort your order.",
    cta: "Tell us what happened",
  },
  abandoned: {
    headline: "Did checkout work?",
    body: "Looks like you tapped Checkout earlier — did everything go through, or did something trip you up?",
    cta: "Share one quick note",
  },
  return_visit: {
    headline: "Welcome back",
    body: "Anything that stopped you buying last time? One line is enough — we read every reply.",
    cta: "Help us fix it",
  },
};

const SS = typeof window !== "undefined" ? window.sessionStorage : null;
const LS = typeof window !== "undefined" ? window.localStorage : null;

function safeGet(storage, k) { try { return storage?.getItem(k) ?? null; } catch { return null; } }
function safeSet(storage, k, v) { try { storage?.setItem(k, v); } catch { /* ignore quota */ } }
function safeRemove(storage, k) { try { storage?.removeItem(k); } catch { /* ignore */ } }

export default function CheckoutFeedback({ cartItems = [], platform, country, sessionId }) {
  const [trigger, setTrigger] = useState(null);          // null | "checkout_error" | "abandoned" | "return_visit"
  const [reason, setReason] = useState(null);
  const [open, setOpen] = useState(false);
  const [hadProblem, setHadProblem] = useState(null);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const dismissedRef = useRef(false);

  // Dismissed-this-session guard. One shot per browser tab lifecycle.
  useEffect(() => {
    dismissedRef.current = safeGet(SS, "tracksy:ckfb:dismissed") === "1";
  }, []);

  // ─── Checkout-error listener (highest priority) ──────────────────────────
  useEffect(() => {
    const onError = (e) => {
      if (dismissedRef.current) return;
      setTrigger("checkout_error");
      setReason(e?.detail?.reason || "unknown");
      setOpen(true);
    };
    window.addEventListener("tracksy:checkout-error", onError);
    return () => window.removeEventListener("tracksy:checkout-error", onError);
  }, []);

  // ─── Return-visit + abandoned-attempt detection on mount ─────────────────
  useEffect(() => {
    if (dismissedRef.current) return;
    if (trigger) return;                                 // error already surfaced

    const nowMs = Date.now();
    const successFlag = safeGet(LS, "tracksy:ck:success");
    const lastAttempt = parseInt(safeGet(LS, "tracksy:ck:lastAttempt") || "0", 10);
    const lastVisit = parseInt(safeGet(LS, "tracksy:ck:lastVisit") || "0", 10);

    // Abandoned attempt: tapped Checkout ≥10 min ago but never completed.
    if (lastAttempt && !successFlag && nowMs - lastAttempt > 10 * 60 * 1000) {
      setTrigger("abandoned");
      setOpen(true);
      // Clear the stamp so we don't nag again next page view.
      safeRemove(LS, "tracksy:ck:lastAttempt");
      return;
    }

    // Return visit: items still in cart + visitor was last here ≥30 min ago.
    if ((cartItems?.length || 0) > 0 && lastVisit && nowMs - lastVisit > 30 * 60 * 1000 && !lastAttempt) {
      setTrigger("return_visit");
      setOpen(true);
    }

    // Always stamp the "last visit" marker so the next return-visit works.
    safeSet(LS, "tracksy:ck:lastVisit", String(nowMs));
  }, [cartItems, trigger]);

  const dismiss = () => {
    setOpen(false);
    safeSet(SS, "tracksy:ckfb:dismissed", "1");
    dismissedRef.current = true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const webview = detectInAppBrowser();
      const payload = {
        session_id: sessionId || safeGet(LS, "tracksy:sid") || null,
        trigger: trigger || "manual",
        had_problem: hadProblem,
        message: message.slice(0, 4000),
        email: email.slice(0, 200),
        platform,
        country,
        cart_snapshot: (cartItems || []).map((i) => ({
          packId: i.packId, colorId: i.colorId || i.color, qty: i.qty, price: i.price,
        })).slice(0, 20),
        webview: webview?.source || null,
        webview_platform: webview?.platform || null,
        checkout_error: reason || null,
      };
      await fetch(`${BACKEND}/api/support/checkout-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      try { window.clarity?.("event", "checkout_feedback_submitted"); } catch {}
      setDone(true);
      // Hide the card after a short "thanks" moment.
      setTimeout(() => dismiss(), 2200);
    } catch {
      // Network failed — close anyway, we already tried.
      dismiss();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !trigger) return null;
  const copy = COPY[trigger] || COPY.manual || COPY.abandoned;

  return (
    <AnimatePresence>
      <motion.div
        key="feedback-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        data-testid="checkout-feedback-card"
        data-trigger={trigger}
        className="fixed bottom-4 right-4 z-[95] w-[min(360px,calc(100vw-2rem))] rounded-2xl border border-black/10 bg-white p-4 shadow-xl sm:bottom-6 sm:right-6"
        role="dialog"
        aria-label={copy.headline}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tracksy-orange/10 text-tracksy-orange">
            <MessageCircleWarning className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-bold text-tracksy-ink">{copy.headline}</p>
            {!done && <p className="mt-1 text-xs leading-relaxed text-tracksy-sub">{copy.body}</p>}
            {done && <p className="mt-1 text-xs leading-relaxed text-tracksy-green">Thanks — logged. We'll follow up if you left an email.</p>}
          </div>
          <button
            type="button"
            data-testid="checkout-feedback-dismiss"
            onClick={dismiss}
            className="shrink-0 rounded-full p-1 text-tracksy-sub transition-colors hover:text-tracksy-ink"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!done && (
          <>
            {hadProblem === null && (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  data-testid="checkout-feedback-yes"
                  onClick={() => setHadProblem(true)}
                  className="flex-1 rounded-full border border-tracksy-ink/15 bg-white px-3 py-2 text-xs font-bold text-tracksy-ink transition-colors hover:border-tracksy-orange hover:text-tracksy-orange"
                >
                  Yes — something went wrong
                </button>
                <button
                  type="button"
                  data-testid="checkout-feedback-no"
                  onClick={() => setHadProblem(false)}
                  className="flex-1 rounded-full border border-tracksy-ink/15 bg-white px-3 py-2 text-xs font-semibold text-tracksy-sub transition-colors hover:border-tracksy-ink hover:text-tracksy-ink"
                >
                  No, it worked
                </button>
              </div>
            )}

            {hadProblem !== null && (
              <div className="mt-3 space-y-2">
                <textarea
                  data-testid="checkout-feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={hadProblem ? "What happened? Any error, step, or button that didn't work" : "Anything we could improve?"}
                  rows={3}
                  className="w-full resize-none rounded-xl border border-tracksy-ink/15 bg-white px-3 py-2 text-xs text-tracksy-ink placeholder:text-tracksy-sub/60 focus:border-tracksy-orange focus:outline-none"
                  maxLength={4000}
                />
                <input
                  data-testid="checkout-feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional — so we can follow up)"
                  className="w-full rounded-full border border-tracksy-ink/15 bg-white px-3 py-2 text-xs text-tracksy-ink placeholder:text-tracksy-sub/60 focus:border-tracksy-orange focus:outline-none"
                  maxLength={200}
                />
                <button
                  type="button"
                  data-testid="checkout-feedback-submit"
                  onClick={submit}
                  disabled={submitting || (!message.trim() && !email.trim())}
                  className="w-full rounded-full bg-tracksy-ink px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide text-white transition-opacity hover:bg-black disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
